"use client"

import { useEffect, useState } from "react"
import type { TreeNode, FileAttachment, FolderStatus, FileStatus } from "../../file-tree-manager/types/tree-types"
import { updateTree, findNodeById } from "../../file-tree-manager/utils/tree-utils"
import { sortTreeNodes } from "../../file-tree-manager/utils/sort-utils"
import { getContentsTree, createContentsNode, updateContentsNode, deleteContentsNode, processingContentsNode } from "../../api/admin"

import { openAlert } from '../../reducer/alert'
import { store } from '../../store/AriStore'

// 서버 DTO -> TreeNode 매핑
function mapDtoToTreeNode(dto: any): TreeNode {
  
  const attachments: FileAttachment[] | undefined = dto.attachments
    ? dto.attachments.map((att: FileAttachment) => ({
        id: String(att.id),
        contentsId: Number(att.contentsId),
        fileName: att.fileName,
        fileSize: Number(att.fileSize ?? 0),
        fileType: att.fileType,
      }))
    : undefined

  const node: TreeNode = {
    id: String(dto.id),
    title: dto.title,
    type: dto.type === "folder" ? "folder" : "file",
    isExpanded: dto.isExpanded ?? false,
    createdAt: dto.createdAt ? new Date(dto.createdAt) : undefined,
    modifiedAt: dto.modifiedAt ? new Date(dto.modifiedAt) : undefined,
    author: dto.author,
    lastModifiedBy: dto.lastModifiedBy,
    contentsManager: dto.contentsManager,
    url: dto.url,
    attachments,
    status: dto.status,
    children: Array.isArray(dto.children) ? dto.children.map(mapDtoToTreeNode) : undefined,
    flowConfig: dto.flowConfig,
  }

  return node
}

// 초기 진입/새로고침 시 첫 번째 루트 폴더를 펼친 상태로 보이도록 조정
function expandFirstRoot(nodes: TreeNode[]): TreeNode[] {
  if (!Array.isArray(nodes) || nodes.length === 0) return nodes
  const first = nodes[0]
  const expandedFirst = first.type === "folder" ? { ...first, isExpanded: true } : first
  return [expandedFirst, ...nodes.slice(1)]
}

export const useTreeData = () => {
  const [treeData, setTreeData] = useState<TreeNode[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const refreshTree = async () => {
    setIsLoading(true)
    try {
      const data = await getContentsTree()
      const mapped: TreeNode[] = Array.isArray(data) ? data.map(mapDtoToTreeNode) : []
      // 첫 번째 노드 펼치기
      // setTreeData(expandFirstRoot(sortTreeNodes(mapped)))
      setTreeData(sortTreeNodes(mapped))
    } catch (e) {
      setTreeData([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true
    ;(async () => {
      try {
        setIsLoading(true)
        const data = await getContentsTree()
        if (!isMounted) return
        const mapped: TreeNode[] = Array.isArray(data) ? data.map(mapDtoToTreeNode) : []
        // 첫 번째 노드 펼치기
        // setTreeData(expandFirstRoot(sortTreeNodes(mapped)))
        setTreeData(sortTreeNodes(mapped))
      } catch (e) {
        if (!isMounted) return
        setTreeData([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    })()
    return () => {
      isMounted = false
    }
  }, [])

  const toggleExpanded = (id: string) => {
    setTreeData((prev) =>
      sortTreeNodes(updateTree(prev, (node) => (node.id === id ? { ...node, isExpanded: !node.isExpanded } : node))),
    )
  }

  const deleteNode = async (id: string) => {
    try {
      await deleteContentsNode(id)
      await refreshTree()
    } catch (e: any) {
      store.dispatch(openAlert({ message: e?.message || "삭제에 실패했습니다." }))
    }
  }

  const updateNode = async (id: string, updates: Partial<TreeNode>) => {
    try {
      const currentNode = findNodeById(treeData, id)
      const mergedNode: TreeNode | null = currentNode ? ({ ...currentNode, ...updates } as TreeNode) : null

      const payload = mergedNode
        ? {
            id: mergedNode.id,
            title: mergedNode.title,
            type: mergedNode.type,
            status: (mergedNode.status as any) ?? undefined,
            url: mergedNode.url,
            contentsManager: mergedNode.contentsManager,
          }
        : {
            // fallback: 최소 변경 필드만 전송
            title: updates.title,
            status: (updates.status as any) ?? undefined,
            url: updates.url,
            contentsManager: updates.contentsManager,
          }

      await updateContentsNode(id, payload)
      await refreshTree()
    } catch (e: any) {
      store.dispatch(openAlert({ message: e?.message || "업데이트에 실패했습니다." }))
    }
  }

  const processingNode = async (id: string, updates: Partial<TreeNode>) => {
    try {
      const currentNode = findNodeById(treeData, id)
      const mergedNode: TreeNode | null = currentNode ? ({ ...currentNode, ...updates } as TreeNode) : null

      const payload = mergedNode
        ? {
            id: mergedNode.id,
            title: mergedNode.title,
            type: mergedNode.type,
            status: (mergedNode.status as any) ?? undefined,
            url: mergedNode.url,
            contentsManager: mergedNode.contentsManager,
          }
        : {
            // fallback: 최소 변경 필드만 전송
            title: updates.title,
            status: (updates.status as any) ?? undefined,
            url: updates.url,
            
            contentsManager: updates.contentsManager,
          }

      await processingContentsNode(id, payload)
      await refreshTree()
    } catch (e: any) {
      const { openAlert } = await import('../../reducer/alert')
      const { store } = await import('../../store/AriStore')
      store.dispatch(openAlert({ message: e?.message || "업데이트에 실패했습니다." }))
    }
  }

  const addNode = async (newNode: TreeNode, parentId?: string) => {
    try {
      const payload = {
        pid: parentId ?? null,
        title: newNode.title,
        type: newNode.type,
        status: newNode.status as any,
        url: newNode.url,
        contentsManager: newNode.contentsManager,
      }
      const created = await createContentsNode(payload)
      // 생성 후 서버에서 전체 트리를 재조회
      await refreshTree()
    } catch (e: any) {
      const { openAlert } = await import('../../reducer/alert')
      const { store } = await import('../../store/AriStore')
      store.dispatch(openAlert({ message: e?.message || "생성에 실패했습니다." }))
    }
  }

  const moveNode = (draggedId: string, targetId: string | null) => {
    const draggedNode = findNodeById(treeData, draggedId)
    if (!draggedNode) return

    const nodeCopy: TreeNode = {
      ...draggedNode,
      children: draggedNode.children ? [...draggedNode.children] : undefined,
      modifiedAt: new Date(),
    }

    setTreeData((prevData) => {
      // Remove the node from its current location
      const removeNode = (nodes: TreeNode[]): TreeNode[] => {
        return nodes.reduce((acc: TreeNode[], node) => {
          if (node.id === draggedId) {
            return acc
          }

          const updatedNode = { ...node }
          if (updatedNode.children) {
            updatedNode.children = removeNode(updatedNode.children)
          }
          acc.push(updatedNode)
          return acc
        }, [])
      }

      let newTreeData = removeNode(prevData)

      // Add the node to the new location
      if (targetId === null) {
        newTreeData = [...newTreeData, nodeCopy]
      } else {
        const addToTarget = (nodes: TreeNode[]): TreeNode[] => {
          return nodes.map((node) => {
            if (node.id === targetId && node.type === "folder") {
              return {
                ...node,
                children: [...(node.children || []), nodeCopy],
                isExpanded: true,
                modifiedAt: new Date(),
              }
            }

            if (node.children) {
              return {
                ...node,
                children: addToTarget(node.children),
              }
            }

            return node
          })
        }

        newTreeData = addToTarget(newTreeData)
      }

      return sortTreeNodes(newTreeData)
    })
  }

  const updateContentsManager = (nodeId: string, contentsManager: string[]) => {
    updateNode(nodeId, { contentsManager })
  }

  const updateUrl = (nodeId: string, url: string) => {
    updateNode(nodeId, { url })
  }

  const updateStatus = (nodeId: string, status: FolderStatus | FileStatus) => {
    processingNode(nodeId, { status })
  }

  const addAttachment = (nodeId: string, attachment: FileAttachment) => {
    const node = findNodeById(treeData, nodeId)
    if (node) {
      const currentAttachments = node.attachments || []
      updateNode(nodeId, { attachments: [...currentAttachments, attachment] })
    }
  }

  const removeAttachment = (nodeId: string, attachmentId: string) => {
    const node = findNodeById(treeData, nodeId)
    if (node && node.attachments) {
      const updatedAttachments = node.attachments.filter((att) => att.id !== attachmentId)
      updateNode(nodeId, { attachments: updatedAttachments })
    }
  }

  return {
    treeData,
    isLoading,
    toggleExpanded,
    deleteNode,
    updateNode,
    addNode,
    moveNode,
    updateContentsManager,
    updateUrl,
    updateStatus,
    addAttachment,
    removeAttachment,
    refreshTree,
  }
}


